import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// props:
// - value: 'YYYY-MM-DD' | ''
// - onChange: (dateStr) => void
// - isDayDisabled?: (dateStr) => boolean
// - minDate?: 'YYYY-MM-DD'
// - maxDate?: 'YYYY-MM-DD'
// - view?: 'month' | 'week' (default 'month')
export default function DatePicker({ value = '', onChange, isDayDisabled, minDate, maxDate, view = 'month' }) {
  const parse = (s) => {
    if (!s) return null;
    const [Y, M, D] = String(s).split('-').map(Number);
    if (!Y || !M || !D) return null;
    return new Date(Date.UTC(Y, M - 1, D));
  };
  const fmt = (d) => {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const todayUTC = useMemo(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  }, []);

  // Calcular inicio y fin de la semana actual (UTC, lunes a domingo)
  const currentWeekStart = useMemo(() => {
    const dow = (todayUTC.getUTCDay() + 6) % 7; // lunes=0
    return new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth(), todayUTC.getUTCDate() - dow));
  }, [todayUTC]);
  const currentWeekEnd = useMemo(() => new Date(Date.UTC(currentWeekStart.getUTCFullYear(), currentWeekStart.getUTCMonth(), currentWeekStart.getUTCDate() + 6)), [currentWeekStart]);

  // Si estamos en vista semana, bloquear al rango de la semana actual
  const min = view === 'week'
    ? currentWeekStart
    : (minDate ? parse(minDate) : todayUTC);
  const max = view === 'week'
    ? currentWeekEnd
    : (maxDate ? parse(maxDate) : null);
  const initial = value ? parse(value) : min;
  const initialCursor = view === 'week'
    ? new Date(Date.UTC(initial.getUTCFullYear(), initial.getUTCMonth(), initial.getUTCDate()))
    : new Date(Date.UTC(initial.getUTCFullYear(), initial.getUTCMonth(), 1));
  const [cursor, setCursor] = useState(initialCursor);

  // Helpers de formato
  const fmtShort = (d) => `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}`;

  // Cálculos para modo mes
  const startDay = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
  const endDay = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0));

  const days = [];
  const offset = (startDay.getUTCDay() + 6) % 7; // Lunes=0 ... Domingo=6
  for (let i = 0; i < offset; i++) days.push(null);
  for (let d = 1; d <= endDay.getUTCDate(); d++) {
    days.push(new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), d)));
  }

  const getWeekStart = (d) => {
    // Lunes = 1 ... Domingo = 7; queremos lunes como inicio
    const dow = (d.getUTCDay() + 6) % 7; // lunes=0
    const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dow));
    return start;
  };
  const weekStart = getWeekStart(cursor);
  const weekDays = Array.from({ length: 7 }, (_, i) => new Date(Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate() + i)));

  const canPrev = () => {
    if (view === 'week') {
      // Bloquear navegación de semanas: no permitir retroceder
      return false;
    }
    const prevMonth = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() - 1, 1));
    return prevMonth >= new Date(Date.UTC(min.getUTCFullYear(), min.getUTCMonth(), 1));
  };
  const canNext = () => {
    if (view === 'week') {
      // Bloquear navegación de semanas: no permitir avanzar
      return false;
    }
    if (!max) return true;
    const nextMonth = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    return nextMonth <= new Date(Date.UTC(max.getUTCFullYear(), max.getUTCMonth(), 1));
  };

  const isDisabled = (d) => {
    if (!d) return true;
    if (d < min) return true;
    if (max && d > max) return true;
    const s = fmt(d);
    return isDayDisabled ? !!isDayDisabled(s) === true : false;
  };

  const selectedStr = value || '';

  const renderHeader = () => {
    if (view === 'week') {
      const label = `${fmtShort(weekDays[0])} – ${fmtShort(weekDays[6])}`;
      return (
        <div className="flex items-center justify-between mb-2">
          <button type="button" className="p-2 rounded-md bg-gray-100 disabled:opacity-40" onClick={() => {}} disabled>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-semibold">{label}</div>
          <button type="button" className="p-2 rounded-md bg-gray-100 disabled:opacity-40" onClick={() => {}} disabled>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-between mb-2">
        <button type="button" className="p-2 rounded-md bg-gray-100 disabled:opacity-40" onClick={() => canPrev() && setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() - 1, 1)))} disabled={!canPrev()}>
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="font-semibold">
          {cursor.toLocaleString('es-CL', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
        </div>
        <button type="button" className="p-2 rounded-md bg-gray-100 disabled:opacity-40" onClick={() => canNext() && setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1)))} disabled={!canNext()}>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const renderGrid = () => {
    if (view === 'week') {
      const dayNamesShort = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];
      return (
        <>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-600 mb-1">
            {weekDays.map((d) => {
              const dow = (d.getUTCDay() + 6) % 7; // lunes=0
              return <div key={`lbl-${d.toISOString()}`}>{dayNamesShort[dow]}</div>;
            })}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((d) => {
              const s = fmt(d);
              const disabled = isDisabled(d);
              const selected = selectedStr === s;
              const dow = (d.getUTCDay() + 6) % 7;
              const dayNum = d.getUTCDate();
              return (
                <button
                  key={s}
                  type="button"
                  aria-label={`${dayNamesShort[dow]} ${dayNum}`}
                  className={`h-14 rounded-md text-sm transition-colors flex flex-col items-center justify-center gap-0.5 ${selected ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' : disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-100 border'} border-gray-200`}
                  onClick={() => !disabled && onChange && onChange(s)}
                  disabled={disabled}
                >
                  <div className={`leading-none ${selected ? 'text-white/90' : 'text-gray-500'}`}>{dayNum}</div>
                </button>
              );
            })}
          </div>
        </>
      );
    }
    return (
      <>
        <div className="grid grid-cols-7 gap-1 text-center text-sm text-gray-600 mb-1">
          <div>L</div><div>M</div><div>X</div><div>J</div><div>V</div><div>S</div><div>D</div>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            if (!d) return <div key={i} className="h-9" />;
            const s = fmt(d);
            const disabled = isDisabled(d);
            const selected = selectedStr === s;
            return (
              <button
                key={s}
                type="button"
                className={`h-9 rounded-md text-sm transition-colors ${selected ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' : disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-100 border'} border-gray-200`}
                onClick={() => !disabled && onChange && onChange(s)}
                disabled={disabled}
              >
                {d.getUTCDate()}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="w-full">
      {renderHeader()}
      {view !== 'week' && (
        <div className="grid grid-cols-7 gap-1 text-center text-sm text-gray-600 mb-1">
          <div>L</div><div>M</div><div>X</div><div>J</div><div>V</div><div>S</div><div>D</div>
        </div>
      )}
      {renderGrid()}
    </div>
  );
}
